import { useEffect, useState } from "react";
import { apiClient, formatApiError, uploadFile, resolveUrl } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Star, Upload, X, Loader2 } from "lucide-react";
import { PageHeader, Card, Field, inputCls, btnPrimary, btnGhost, btnDanger } from "@/components/admin/ui";

const empty = { title: "", category: "custom_furniture", description: "", cover_image: "", images: [], videos: [], featured: false };
const cats = CATEGORIES.filter((c) => c.value !== "all");

export default function ProjectsPage() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchItems = () => {
        setLoading(true);
        apiClient.get("/projects").then((r) => setItems(r.data)).finally(() => setLoading(false));
    };
    useEffect(fetchItems, []);

    const openCreate = () => { setEditing({ ...empty }); setOpen(true); };
    const openEdit = (p) => { setEditing({ ...p }); setOpen(true); };

    const del = async (id) => {
        if (!window.confirm("Delete this project?")) return;
        await apiClient.delete(`/projects/${id}`);
        toast.success("Deleted");
        fetchItems();
    };

    const toggleFeatured = async (p) => {
        await apiClient.patch(`/projects/${p.id}`, { featured: !p.featured });
        fetchItems();
    };

    return (
        <>
            <PageHeader
                title="Projects"
                subtitle="Showcase finished work with multiple images and videos."
                action={<button onClick={openCreate} data-testid="add-project-btn" className={btnPrimary}><Plus size={16} /> New project</button>}
            />

            {loading ? <Loader2 className="animate-spin text-walnut" /> : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {items.map((p) => (
                        <Card key={p.id} className="overflow-hidden" data-testid={`project-row-${p.id}`}>
                            <div className="aspect-[4/3] bg-sand dark:bg-stone-800 relative">
                                {p.cover_image ? (
                                    <img src={resolveUrl(p.cover_image)} alt={p.title} className="w-full h-full object-cover" />
                                ) : <div className="w-full h-full flex items-center justify-center text-stone-muted text-xs">No cover</div>}
                                {p.featured && <div className="absolute top-2 left-2 bg-walnut text-cream text-[10px] uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1"><Star size={10} /> Featured</div>}
                            </div>
                            <div className="p-4">
                                <div className="font-serif text-xl">{p.title}</div>
                                <div className="text-[10px] uppercase tracking-widest text-walnut-light mt-1">{p.category}</div>
                                <p className="text-xs text-stone-muted dark:text-stone-400 mt-2 line-clamp-2">{p.description}</p>
                                <div className="mt-3 text-[11px] text-stone-muted">{p.images?.length || 0} images · {p.videos?.length || 0} videos</div>
                                <div className="flex gap-2 mt-4 flex-wrap">
                                    <button onClick={() => openEdit(p)} className={btnGhost}><Edit3 size={14} /> Edit</button>
                                    <button onClick={() => toggleFeatured(p)} className={btnGhost}><Star size={14} /> {p.featured ? "Unfeature" : "Feature"}</button>
                                    <button onClick={() => del(p.id)} data-testid={`project-delete-${p.id}`} className={btnDanger}><Trash2 size={14} /></button>
                                </div>
                            </div>
                        </Card>
                    ))}
                    {!items.length && <div className="text-stone-muted col-span-full py-12">No projects yet. Create the first one.</div>}
                </div>
            )}

            {open && editing && (
                <ProjectModal
                    initial={editing}
                    onClose={() => setOpen(false)}
                    onSaved={() => { setOpen(false); fetchItems(); }}
                />
            )}
        </>
    );
}

function ProjectModal({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const isEdit = !!initial.id;

    const handleUpload = async (e, target) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const urls = [];
            for (const f of files) {
                const r = await uploadFile(f);
                urls.push(r.url);
            }
            if (target === "cover") setForm({ ...form, cover_image: urls[0] });
            else setForm({ ...form, images: [...(form.images || []), ...urls] });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const removeImage = (i) => setForm({ ...form, images: form.images.filter((_, idx) => idx !== i) });
    const addVideo = () => {
        const url = window.prompt("YouTube URL:");
        if (url) setForm({ ...form, videos: [...(form.videos || []), url] });
    };
    const removeVideo = (i) => setForm({ ...form, videos: form.videos.filter((_, idx) => idx !== i) });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isEdit) await apiClient.patch(`/projects/${form.id}`, form);
            else await apiClient.post("/projects", form);
            toast.success(isEdit ? "Updated" : "Created");
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-4 overflow-auto">
            <form onSubmit={submit} className="bg-cream dark:bg-[#171311] w-full max-w-3xl p-6 md:p-8 my-6" data-testid="project-form">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-walnut-light">{isEdit ? "Edit" : "New"} project</div>
                        <h2 className="font-serif text-3xl mt-1">{form.title || "Untitled"}</h2>
                    </div>
                    <button type="button" onClick={onClose}><X /></button>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Title" required><input data-testid="project-title" required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
                    <Field label="Category" required>
                        <select data-testid="project-category" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                            {cats.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Description" className="md:col-span-2"><textarea rows={3} className={inputCls + " resize-none"} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>

                    <Field label="Cover image" className="md:col-span-2">
                        <div className="flex gap-3 items-start">
                            {form.cover_image && <img src={resolveUrl(form.cover_image)} alt="" className="w-24 h-24 object-cover bg-sand" />}
                            <div className="flex-1 space-y-2">
                                <input className={inputCls} placeholder="https:// or upload below" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} />
                                <label className={btnGhost + " cursor-pointer w-fit"}>
                                    <Upload size={14} /> Upload cover
                                    <input type="file" accept="image/*" onChange={(e) => handleUpload(e, "cover")} className="hidden" />
                                </label>
                            </div>
                        </div>
                    </Field>

                    <Field label={`Additional images (${form.images?.length || 0})`} className="md:col-span-2">
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                            {(form.images || []).map((u, i) => (
                                <div key={i} className="relative aspect-square bg-sand">
                                    <img src={resolveUrl(u)} alt="" className="w-full h-full object-cover" />
                                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/70 text-white p-1"><X size={12} /></button>
                                </div>
                            ))}
                            <label className={btnGhost + " aspect-square flex flex-col items-center justify-center cursor-pointer"}>
                                {uploading ? <Loader2 className="animate-spin" size={16} /> : <><Upload size={14} /><span className="text-[10px] mt-1">Upload</span></>}
                                <input type="file" accept="image/*" multiple onChange={(e) => handleUpload(e, "images")} className="hidden" />
                            </label>
                        </div>
                    </Field>

                    <Field label="YouTube videos" className="md:col-span-2">
                        <div className="space-y-2">
                            {(form.videos || []).map((v, i) => (
                                <div key={i} className="flex gap-2 items-center bg-white dark:bg-[#0e0c0a] border border-stone-border dark:border-stone-700 px-3 py-2 text-xs">
                                    <span className="flex-1 truncate">{v}</span>
                                    <button type="button" onClick={() => removeVideo(i)}><X size={14} /></button>
                                </div>
                            ))}
                            <button type="button" onClick={addVideo} className={btnGhost}><Plus size={14} /> Add YouTube link</button>
                        </div>
                    </Field>

                    <label className="md:col-span-2 inline-flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-walnut" />
                        Mark as featured project
                    </label>
                </div>

                <div className="flex gap-3 mt-8">
                    <button type="submit" disabled={saving} className={btnPrimary} data-testid="project-save-btn">
                        {saving && <Loader2 className="animate-spin" size={14} />} {isEdit ? "Save changes" : "Create project"}
                    </button>
                    <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
                </div>
            </form>
        </div>
    );
}
